import React, { useEffect, useRef } from 'react';
import './BottomSheet.css';

// Generalized bottom sheet for the Lesson Dashboard — the SortFilterSheet
// pattern (Portal Work v5) lifted into a reusable shell: role="dialog" +
// aria-modal, focus trap, Esc + scrim dismiss, body scroll lock, focus
// returned to the opener. 22px top radius + grab handle per the design
// handoff; max-height 88% (the wrap-up sheet's spec) with an internal
// scroll region.
//
// Content is children; the sheet owns only chrome + a11y. Promote to
// src/components/Portal/ only when a third consumer appears (CLAUDE.md:
// no gratuitous refactors).

const FOCUSABLE =
	'a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Past this many px of downward drag, releasing dismisses the sheet instead
// of springing back (device pass 2026-07-23: the grab handle was decorative —
// no drag gesture was ever wired to it, so closing required tapping the scrim).
const DISMISS_THRESHOLD_PX = 90;

function BottomSheet({ title, titleId = 'ld-sheet-title', onClose, header = null, footer = null, children, className = '' }) {
	const panelRef = useRef(null);
	const grabRef = useRef(null);
	const headRef = useRef(null);
	const openerRef = useRef(null);
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	// Capture the opener so focus returns there on close; lock body scroll
	// while the sheet is up. Move initial focus into the panel.
	useEffect(() => {
		openerRef.current =
			document.activeElement instanceof HTMLElement ? document.activeElement : null;
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		const raf = requestAnimationFrame(() => {
			const first = panelRef.current?.querySelector('.ld-sheet__close');
			if (first) first.focus();
		});
		return () => {
			cancelAnimationFrame(raf);
			document.body.style.overflow = prevOverflow;
			if (openerRef.current) openerRef.current.focus();
		};
	}, []);

	// Drag-to-dismiss — native touch listeners, not React's synthetic pointer
	// events (device pass 2026-07-23 round 2: the pointer-event + CSS
	// touch-action version still didn't drag on a real iPhone). Native
	// { passive: false } + an explicit preventDefault is the reliable way to
	// stop iOS from racing this gesture against its own scroll/bounce
	// handling; the panel's transform is set imperatively (not via React
	// state) so the drag tracks the finger every frame instead of waiting on
	// a render. Scoped to the grab handle + header so it never fights the
	// content region's own vertical scroll.
	useEffect(() => {
		const panel = panelRef.current;
		const targets = [grabRef.current, headRef.current].filter(Boolean);
		if (!panel || !targets.length) return undefined;

		let startY = null;
		let dragging = false;

		const setTransform = (y) => {
			panel.style.transition = 'none';
			panel.style.transform = y > 0 ? `translateY(${y}px)` : '';
		};

		const release = (endY) => {
			dragging = false;
			panel.style.transition = '';
			const delta = startY == null ? 0 : endY - startY;
			startY = null;
			if (delta > DISMISS_THRESHOLD_PX) {
				panel.style.transform = `translateY(${window.innerHeight}px)`;
				setTimeout(() => onCloseRef.current(), 200);
			} else {
				panel.style.transform = '';
			}
		};

		const onTouchStart = (e) => {
			if (e.touches.length !== 1) return; // let pinch-zoom alone
			startY = e.touches[0].clientY;
			dragging = true;
		};
		const onTouchMove = (e) => {
			if (!dragging) return;
			e.preventDefault(); // claim the gesture once it starts here
			setTransform(e.touches[0].clientY - startY);
		};
		const onTouchEnd = (e) => {
			if (!dragging) return;
			release(e.changedTouches[0]?.clientY ?? startY);
		};

		// Desktop/mouse fallback (dev + trackpad testing).
		const onMouseDown = (e) => {
			startY = e.clientY;
			dragging = true;
			const onMouseMove = (me) => {
				if (dragging) setTransform(me.clientY - startY);
			};
			const onMouseUp = (me) => {
				window.removeEventListener('mousemove', onMouseMove);
				window.removeEventListener('mouseup', onMouseUp);
				release(me.clientY);
			};
			window.addEventListener('mousemove', onMouseMove);
			window.addEventListener('mouseup', onMouseUp);
		};

		targets.forEach((el) => {
			el.addEventListener('touchstart', onTouchStart, { passive: true });
			el.addEventListener('touchmove', onTouchMove, { passive: false });
			el.addEventListener('touchend', onTouchEnd);
			el.addEventListener('touchcancel', onTouchEnd);
			el.addEventListener('mousedown', onMouseDown);
		});
		return () => {
			targets.forEach((el) => {
				el.removeEventListener('touchstart', onTouchStart);
				el.removeEventListener('touchmove', onTouchMove);
				el.removeEventListener('touchend', onTouchEnd);
				el.removeEventListener('touchcancel', onTouchEnd);
				el.removeEventListener('mousedown', onMouseDown);
			});
		};
	}, []);

	const onKeyDown = (e) => {
		if (e.key === 'Escape') {
			e.preventDefault();
			onClose();
			return;
		}
		if (e.key !== 'Tab') return;
		// Focus trap: wrap Tab / Shift+Tab within the panel.
		const nodes = panelRef.current
			? Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter(
					(n) => n.offsetParent !== null
			  )
			: [];
		if (!nodes.length) return;
		const first = nodes[0];
		const last = nodes[nodes.length - 1];
		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	};

	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions
		<div className="ld-sheet-overlay" onKeyDown={onKeyDown}>
			{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
			<div className="ld-sheet__scrim" onClick={onClose} />
			<div
				ref={panelRef}
				className={`ld-sheet ${className}`.trim()}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}>
				<span ref={grabRef} className="ld-sheet__grab" aria-hidden="true" />
				<div ref={headRef} className="ld-sheet__head">
					<h2 className="ld-sheet__title" id={titleId}>
						{title}
					</h2>
					{header}
					<button
						type="button"
						className="ld-sheet__close"
						aria-label={`Close ${typeof title === 'string' ? title.toLowerCase() : 'sheet'}`}
						onClick={onClose}>
						<i className="fa-sharp fa-solid fa-xmark" />
					</button>
				</div>

				<div className="ld-sheet__scroll">{children}</div>

				{footer && <div className="ld-sheet__foot">{footer}</div>}
			</div>
		</div>
	);
}

export default BottomSheet;
